import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  computed,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  HostListener,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { GoodsDetailsVO } from '@app/api/goods-service';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { BarcodeTableComponent } from '../barcode-table/barcode-table.component';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import { SKUDetail, SKUAttribute, Barcode } from '../../models/sku.interface';
import { SKUService } from '../../services/sku.service';
import { SyncLogItem } from '../../../sync-report/sync-report.config';
import {
  SKU_STATUSES,
  getSKUStatusByCode,
  GOODS_ATTRIBUTE_SOURCES_JSON,
  ATTRIBUTE_SECTION_LABELS,
  AttributeSourceType,
} from '../../models/sku.constants';
import { SpuApiService } from '../../../spu/services/spu-api.service';
import { PermissionService } from '@app/core/services/permission.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { TaxCategoryService } from '@app/feature/configuration/product/tax-category/services/tax-category.service';
import { ProductTaxInheritedValue } from '../../../shared/product-tax/product-tax.model';
import {
  deriveProductTaxMode,
  formatProductTaxCategoryDisplay,
} from '../../../shared/product-tax/product-tax.util';
import { SkuTaxResolutionService } from '../../../shared/product-tax/sku-tax-resolution.service';

interface PackageSpuAttribute {
  name: string;
  value: string;
}

type GoodsDetailsWithTax = GoodsDetailsVO & {
  taxId?: number | null;
  taxCode?: string | null;
  taxRate?: number | null;
  isPriceIncludeVAT?: boolean | null;
};

@Component({
  selector: 'app-sku-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    CardModule,
    DividerModule,
    AppButtonComponent,
    TagModule,
    TableModule,
    TooltipModule,
    BarcodeTableComponent,
    ConfirmDialogModule,
    ToastModule,
    DialogModule,
    TextareaModule,
  ],
  templateUrl: './sku-detail.component.html',
  styleUrl: './sku-detail.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService, MessageService],
})
export class SKUDetailComponent implements AfterViewInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly skuService = inject(SKUService);
  private readonly spuApiService = inject(SpuApiService);
  private readonly taxCategoryService = inject(TaxCategoryService);
  private readonly skuTaxResolutionService = inject(SkuTaxResolutionService);
  private readonly elementRef = inject(ElementRef);
  private readonly permissionService = inject(PermissionService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    { initialValue: this.permissionService.getCurrentPermissions() },
  );

  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.sku.edit');
  });

  // Activate/Deactivate is gated on the approve permission, matching the
  // status-toggle endpoints on Brand/Category (OPA: pim_approve).
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.sku.approve');
  });
  private scrollContentEl: HTMLElement | null = null;
  private readonly scrollHandler = () => {
    this.isScrolled.set((this.scrollContentEl?.scrollTop ?? 0) > 10);
  };

  skuItem = signal<SKUDetail | null>(null);
  loading = signal(false);
  isScrolled = signal(false);
  packageSpuAttributes = signal<PackageSpuAttribute[]>([]);
  syncHistory = signal<SyncLogItem[]>([]);
  syncHistoryLoading = signal(false);
  taxCategoryDisplayById = signal<Map<number, string>>(new Map());
  inheritedTaxValue = signal<ProductTaxInheritedValue | null>(null);

  constructor() {
    // Angular's default RouteReuseStrategy reuses this component when navigating between two
    // instances of the same route config (goods/sku/:type/view/:id with a different :id) - e.g.
    // from the chat widget's product cards. Reading route.snapshot once in the constructor would
    // only ever show the FIRST sku; subscribing to paramMap re-runs loadSkuItem() on every such
    // navigation, including the initial one (paramMap replays the current value on subscribe).
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe(() => this.loadSkuItem());
  }

  ngAfterViewInit(): void {
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      this.setupScrollListener();
    }, 100);
  }

  @HostListener('window:resize')
  onResize(): void {
    this.setupScrollListener();
  }

  private setupScrollListener(): void {
    const contentElement = this.elementRef.nativeElement.querySelector(
      '.sku-detail-content',
    );
    if (contentElement) {
      if (this.scrollContentEl) {
        this.scrollContentEl.removeEventListener('scroll', this.scrollHandler);
      }
      this.scrollContentEl = contentElement;
      contentElement.addEventListener('scroll', this.scrollHandler);
    }
  }

  private currentSkuType(): 'ordinary' | 'package' {
    return this.route.snapshot.paramMap.get('type') === 'package'
      ? 'package'
      : 'ordinary';
  }

  ngOnDestroy(): void {
    this.scrollContentEl?.removeEventListener('scroll', this.scrollHandler);
  }

  private loadSkuItem(): void {
    this.loading.set(true);
    const skuId = this.route.snapshot.paramMap.get('id');
    if (skuId) {
      this.skuService.getSkuDetailById(skuId).subscribe({
        next: (item) => {
          this.skuItem.set(item);
          this.loadEffectiveTaxDisplay(item);
          if (item.setFlag === 1) {
            this.loadPackageSpuAttributes(Number(item.spuId));
          } else {
            this.packageSpuAttributes.set([]);
          }
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading SKU item:', error);
          this.loading.set(false);
        },
      });
      this.loadSyncHistory(Number(skuId));
    }
  }

  private loadSyncHistory(skuId: number): void {
    if (!skuId) {
      this.syncHistory.set([]);
      return;
    }

    this.syncHistoryLoading.set(true);
    this.skuService.getSkuSyncHistory(skuId).subscribe({
      next: (response) => {
        this.syncHistory.set(response.data);
        this.syncHistoryLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading SKU sync history:', error);
        this.syncHistory.set([]);
        this.syncHistoryLoading.set(false);
      },
    });
  }

  private loadPackageSpuAttributes(spuId: number): void {
    if (!spuId) {
      this.packageSpuAttributes.set([]);
      return;
    }

    this.spuApiService.getSpuById(spuId).subscribe({
      next: (spu) => {
        const attributes = (spu?.attrLists || [])
          .filter((attribute) => !attribute.isSku)
          .map((attribute) => ({
            name: attribute.attributeName,
            value: (attribute.values || [])
              .map((value) => value.value)
              .filter((value): value is string => Boolean(value))
              .join(', '),
          }))
          .filter((attribute) => attribute.name && attribute.value);

        this.packageSpuAttributes.set(attributes);
      },
      error: () => {
        this.packageSpuAttributes.set([]);
      },
    });
  }

  // Same rule as SKU Management — only Effective SKUs can be
  // activated; any Effective SKU can be deactivated. Never touches status.
  canActivate = computed(() => {
    const item = this.skuItem();
    return (
      !!item &&
      item.status === SKU_STATUSES.EFFECTIVE.code &&
      item.showFlag !== 1
    );
  });

  canDeactivate = computed(() => {
    const item = this.skuItem();
    return (
      !!item &&
      item.status === SKU_STATUSES.EFFECTIVE.code &&
      item.showFlag === 1
    );
  });

  onActivate(): void {
    const item = this.skuItem();
    if (!item || !this.canActivate()) {
      return;
    }

    this.confirmationService.confirm({
      key: 'skuDetailConfirm',
      header: this.translateService.instant(
        'sku_management.confirmations.activate_title',
      ),
      message: this.translateService.instant(
        'sku_management.confirmations.activate_single',
        { skuName: item.skuName },
      ),
      acceptLabel: this.translateService.instant(
        'sku_management.confirmations.activate',
      ),
      rejectLabel: this.translateService.instant(
        'sku_management.confirmations.cancel',
      ),
      acceptButtonStyleClass: 'p-button-success',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      blockScroll: true,
      closeOnEscape: true,
      dismissableMask: true,
      accept: () => this.toggleActivation(true),
    });
  }

  onDeactivate(): void {
    if (!this.canDeactivate()) {
      return;
    }
    this.deactivateReason.set('');
    this.showDeactivateReasonDialog.set(true);
  }

  // Deactivate goes through a reason-capture dialog instead of the plain
  // confirm dialog, matching SKU Management (list page); reason is optional
  // here since no mandatory-reason business rule has been defined yet.
  showDeactivateReasonDialog = signal(false);
  deactivateReason = signal('');

  onConfirmDeactivate(): void {
    this.showDeactivateReasonDialog.set(false);
    this.toggleActivation(false, this.deactivateReason().trim() || undefined);
  }

  onCancelDeactivateDialog(): void {
    this.showDeactivateReasonDialog.set(false);
  }

  private toggleActivation(active: boolean, reason?: string): void {
    const item = this.skuItem();
    if (!item) {
      return;
    }
    const skuId = parseInt(item.id, 10);
    this.skuService.toggleSkuActivation([skuId], active, reason).subscribe({
      next: (results) => {
        const failed = results.filter((r) => !r.success);
        if (failed.length === 0) {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              active
                ? 'sku_management.messages.activate_successful'
                : 'sku_management.messages.deactivate_successful',
            ),
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: failed.map((f) => f.message).join('; '),
          });
        }
        // Reload authoritative state from backend rather than assuming success.
        this.loadSkuItem();
      },
      error: (error) => {
        const apiMessage = error?.error?.message;
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail:
            apiMessage ||
            this.translateService.instant(
              active
                ? 'sku_management.messages.activate_failed'
                : 'sku_management.messages.deactivate_failed',
            ),
        });
      },
    });
  }

  onBack(): void {
    // Get the current query parameters from the route
    const currentQueryParams = this.route.snapshot.queryParams;

    // Remove the 'id' parameter when navigating back to list page
    const { id, ...filterParams } = currentQueryParams;

    // Navigate back to the list page with preserved filter parameters
    this.router.navigate(['/goods/sku', this.currentSkuType()], {
      queryParams: filterParams,
    });
  }

  getStatusSeverity(status: number): 'warning' | 'success' | 'danger' | 'info' {
    const statusObj = getSKUStatusByCode(status);

    if (!statusObj) return 'warning';

    switch (statusObj.code) {
      case SKU_STATUSES.EFFECTIVE.code:
        return 'success';
      case SKU_STATUSES.REJECTED.code:
      case SKU_STATUSES.EFFECTIVE_REJECTED.code:
        return 'danger';
      case SKU_STATUSES.UNDER_REVIEW.code:
      case SKU_STATUSES.EFFECTIVE_EDITING.code:
        return 'warning';
      default:
        return 'warning';
    }
  }

  getStatusDisplayText(status: number): string {
    const statusObj = getSKUStatusByCode(status);

    if (!statusObj) return status.toString();

    switch (statusObj.code) {
      case SKU_STATUSES.EFFECTIVE.code:
        return 'sku_management.status.effective';
      case SKU_STATUSES.REJECTED.code:
        return 'sku_management.status.rejected';
      case SKU_STATUSES.UNDER_REVIEW.code:
        return 'sku_management.status.under_review';
      case SKU_STATUSES.EFFECTIVE_EDITING.code:
        return 'sku_management.status.effective_editing';
      case SKU_STATUSES.EFFECTIVE_REJECTED.code:
        return 'sku_management.status.effective_rejected';
      default:
        return status.toString();
    }
  }

  getStatusIcon(status: number): string {
    const statusObj = getSKUStatusByCode(status);

    if (!statusObj) return 'pi pi-question-circle text-gray-500';

    switch (statusObj.code) {
      case SKU_STATUSES.EFFECTIVE.code:
        return 'pi pi-check-circle text-green-500';
      case SKU_STATUSES.REJECTED.code:
      case SKU_STATUSES.EFFECTIVE_REJECTED.code:
        return 'pi pi-times-circle text-red-500';
      case SKU_STATUSES.UNDER_REVIEW.code:
        return 'pi pi-clock text-orange-500';
      case SKU_STATUSES.EFFECTIVE_EDITING.code:
        return 'pi pi-pencil text-orange-500';
      default:
        return 'pi pi-question-circle text-gray-500';
    }
  }

  getStatusStyle(status: number): any {
    const statusObj = getSKUStatusByCode(status);

    if (!statusObj) return {};

    switch (statusObj.code) {
      case SKU_STATUSES.EFFECTIVE.code:
        return {
          'background-color': '#ecfdf5',
          color: '#047857',
          border: 'none',
        };
      case SKU_STATUSES.REJECTED.code:
      case SKU_STATUSES.EFFECTIVE_REJECTED.code:
        return {
          'background-color': '#fef2f2',
          color: '#dc2626',
          border: 'none',
        };
      case SKU_STATUSES.UNDER_REVIEW.code:
      case SKU_STATUSES.EFFECTIVE_EDITING.code:
        return {
          'background-color': '#fef3c7',
          color: '#a16207',
          border: 'none',
        };
      default:
        return {};
    }
  }

  formatDateTime = formatDateTime;

  getSyncResultSeverity(result: string): 'success' | 'danger' {
    return result === 'SUCCESS' ? 'success' : 'danger';
  }

  getAttributesByType(attributeType: AttributeSourceType): SKUAttribute[] {
    const item = this.skuItem();
    if (!item?.skuAttributeList) return [];

    return item.skuAttributeList.filter(
      (attr) => (attr.source ?? 0) === attributeType,
    );
  }

  hasAttributesForType(attributeType: AttributeSourceType): boolean {
    return this.getAttributesByType(attributeType).length > 0;
  }

  getAttributeSectionLabel(attributeType: AttributeSourceType): string {
    return ATTRIBUTE_SECTION_LABELS[attributeType];
  }

  getDynamicAttributeTypes(): AttributeSourceType[] {
    return [
      GOODS_ATTRIBUTE_SOURCES_JSON.SPU,
      GOODS_ATTRIBUTE_SOURCES_JSON.CLASSIFY,
      GOODS_ATTRIBUTE_SOURCES_JSON.ATTACHED,
    ];
  }

  private loadEffectiveTaxDisplay(item: SKUDetail): void {
    this.skuTaxResolutionService.resolve(item.skuCode).subscribe({
      next: (resolution) => {
        this.inheritedTaxValue.set(resolution.effective);
        this.loadTaxCategoryDisplay(resolution.effective.taxId);
      },
      error: () => this.loadInheritedTaxFromSpu(Number(item.spuId) || null),
    });
  }

  private loadInheritedTaxFromSpu(spuId: number | null): void {
    if (!spuId) {
      this.inheritedTaxValue.set(this.getUnconfiguredInheritedTaxValue());
      return;
    }

    this.spuApiService.getSpuById(spuId).subscribe({
      next: (spu) => {
        const spuWithTax = spu as GoodsDetailsWithTax | null;
        const taxId = spuWithTax?.taxId ?? null;
        const isPriceIncludeVAT = spuWithTax?.isPriceIncludeVAT ?? null;

        this.inheritedTaxValue.set({
          taxId,
          taxCode: spuWithTax?.taxCode ?? null,
          taxRate: spuWithTax?.taxRate ?? null,
          isPriceIncludeVAT,
          source:
            deriveProductTaxMode(taxId, isPriceIncludeVAT) === 'OVERRIDE'
              ? 'SPU_OVERRIDE'
              : 'UNCONFIGURED',
        });
        this.loadTaxCategoryDisplay(taxId);
      },
      error: () => {
        this.inheritedTaxValue.set(this.getUnconfiguredInheritedTaxValue());
      },
    });
  }

  private getUnconfiguredInheritedTaxValue(): ProductTaxInheritedValue {
    return {
      taxId: null,
      taxCode: null,
      taxRate: null,
      isPriceIncludeVAT: null,
      source: 'UNCONFIGURED',
    };
  }

  private loadTaxCategoryDisplay(taxId: number | null): void {
    if (!taxId) {
      return;
    }

    this.taxCategoryService.get(taxId).subscribe({
      next: (taxCategory) => {
        this.taxCategoryDisplayById.update((current) => {
          const next = new Map(current);
          next.set(taxId, formatProductTaxCategoryDisplay(taxCategory));
          return next;
        });
      },
    });
  }

  getTaxDisplayValue(item: SKUDetail): string {
    const inheritedTaxValue = this.inheritedTaxValue();
    const taxId = item.taxId ?? inheritedTaxValue?.taxId ?? null;
    if (taxId) {
      const loadedDisplay = this.taxCategoryDisplayById().get(taxId);
      if (loadedDisplay) {
        return loadedDisplay;
      }
    }

    return formatProductTaxCategoryDisplay({
      taxCode: item.taxCode ?? inheritedTaxValue?.taxCode,
    });
  }

  getVatDisplayKey(item: SKUDetail): string {
    const isPriceIncludeVAT =
      item.isPriceIncludeVAT ?? this.inheritedTaxValue()?.isPriceIncludeVAT;

    if (isPriceIncludeVAT === true) {
      return 'common.options.yes';
    }
    if (isPriceIncludeVAT === false) {
      return 'common.options.no';
    }
    return 'sku_management.detail.values.na';
  }

  openImageModal(imageUrl: string): void {
    // For now, just open the image in a new tab
    // In a real implementation, you might want to use a modal dialog
    window.open(imageUrl, '_blank');
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    // Set a fallback image with a simple placeholder
    img.src =
      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxjaXJjbGUgY3g9IjIwMCIgY3k9IjE1MCIgcj0iNTAiIGZpbGw9IiM5Q0EzQUYiLz4KPHJlY3QgeD0iMTUwIiB5PSIyMDAiIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjOUNBM0FGIi8+Cjwvc3ZnPgo=';
    img.alt = 'Image not available';
    img.classList.add('opacity-50');
  }

  /**
   * Check if the SKU item has any image data to display
   */
  hasImageData(item: SKUDetail): boolean {
    return this.hasMainImages(item) || this.hasDetailImages(item);
  }

  /**
   * Check if main images exist and are valid
   */
  hasMainImages(item: SKUDetail): boolean {
    return !!(
      item.materialMainImages &&
      Array.isArray(item.materialMainImages) &&
      item.materialMainImages.length > 0
    );
  }

  /**
   * Check if detail images exist and are valid
   */
  hasDetailImages(item: SKUDetail): boolean {
    return !!(
      item.imageList &&
      Array.isArray(item.imageList) &&
      item.imageList.length > 0
    );
  }
}
